// @ts-check
import { useState } from 'react';
import { TextField, Typography } from '@mui/material';
import { updateOrganization } from '../../../../repository/UserDAO';
import LoadingData from '../../../../components/loading/LoadingData';
import { useToast } from '../../../../context_providers/toast_context/ToastContext';
import COLORS from '../../../../resources/style/Themes';
import BaseModal from '../../../../components/modals/BaseModal';
import { useTranslation } from 'react-i18next';
import {
  ErrorStringKeys,
  StringKeys,
  SuccessStringKeys,
} from '../../../../resources/strings/StringKeys';
import {
  getIfRequiresPin,
  getGroups,
} from '../../../../repository/OrganizationDAO';
import EnterPinModal from '../../sign_up/modals/EnterPinModal';
import EnterGroupModal from '../../sign_up/modals/EnterGroupModal';
import { JoinRequestCreatedResponse } from '../../../../../generated-client/models';

function UpdateOrganizationModal({
  show,
  onHide,
  currentOrgData,
  onPendingApproval,
}: {
  show: boolean;
  onHide: () => void;
  currentOrgData: {
    org_code: string;
    old_organization_azure_id: string;
    org_user_azure_id: string;
    user_history_azure_id: string;
    user_private_settings_azure_id: string;
  };
  onPendingApproval?: (joinRequest: JoinRequestCreatedResponse) => void;
}) {
  const [newOrgCode, setNewOrgCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [countdown, setCountdown] = useState<number>(0);
  const [groups, setGroups] = useState<string[]>([]);
  const [pin, setPin] = useState<string | null>(null);
  const { showErrorToast, showSuccessToast } = useToast();
  const { t } = useTranslation();

  const handleUpdateOrg = async () => {
    if (!newOrgCode.trim()) {
      showErrorToast(t(StringKeys.PLEASE_ENTER_A_VALID_ORGANIZATION_CODE));
      return;
    }

    setLoading(true);
    try {
      const requiresPin = await getIfRequiresPin(newOrgCode);

      if (requiresPin) {
        setShowPinModal(true);
      } else {
        handleEnterPin(null);
      }
    } catch (error) {
      console.error('Error in handleUpdateOrg:', error);
      showErrorToast(t(StringKeys.SOMETHING_WENT_WRONG));
    } finally {
      setLoading(false);
    }
  };

  const handleEnterPin = async (enteredPin: string | null) => {
    setPin(enteredPin);
    setLoading(true);
    try {
      const groupsData = await getGroups(newOrgCode, enteredPin ?? undefined);

      setGroups(groupsData);
      if (groupsData.length > 1) {
        setShowGroupModal(true);
      } else {
        handleEnterGroup(groupsData[0]);
      }
    } catch (error) {
      console.error('Error in handleEnterPin:', error);
      showErrorToast(t(StringKeys.SOMETHING_WENT_WRONG));
    } finally {
      setLoading(false);
      setShowPinModal(false);
    }
  };

  const handleEnterGroup = async (selectedGroup: string) => {
    setLoading(true);
    try {
      const updateData = {
        org_code: currentOrgData.org_code,
        new_org_code: newOrgCode,
        join_org_pin: pin,
        group_name: selectedGroup,
        skip_approval_check: false,
        old_organization_azure_id: currentOrgData.old_organization_azure_id,
        org_user_azure_id: currentOrgData.org_user_azure_id,
        user_history_azure_id: currentOrgData.user_history_azure_id,
        user_private_settings_azure_id:
          currentOrgData.user_private_settings_azure_id,
      };

      const response = await updateOrganization(updateData);

      if (response.status === 'pending_approval' && response.join_request) {
        // Close modal and notify parent about pending approval
        onHide();
        onPendingApproval?.(response.join_request);
      } else if (response.status === 'organization_changed') {
        // Current flow - show success and refresh
        showSuccessToast(
          t(SuccessStringKeys.SUCCESSFULLY_UPDATED_YOUR_ORGANIZATION),
        );
        startCountdown();
      }
    } catch (error) {
      console.error(error);
      showErrorToast(
        t(ErrorStringKeys.FAILED_TO_UPDATE_ORGANIZATION, {
          organization: newOrgCode,
        }),
      );
    } finally {
      setLoading(false);
      setShowGroupModal(false);
    }
  };

  const startCountdown = () => {
    setCountdown(5);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.reload();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleClose = () => {
    if (countdown === 0) {
      onHide();
    }
  };

  return (
    <>
      <BaseModal
        footerContent={undefined}
        open={show}
        onClose={handleClose}
        title={t(StringKeys.CHANGE_ORGANIZATION)}
        onSubmit={handleUpdateOrg}
        submitButtonText={t(StringKeys.SUBMIT)}
        size="sm"
        enableEnterKey={true}
        showFooter={countdown === 0}
      >
        <TextField
          fullWidth
          label={t(StringKeys.ORGANIZATION_CODE)}
          value={newOrgCode}
          onChange={(e) => setNewOrgCode(e.target.value.toUpperCase())}
          slotProps={{ htmlInput: { maxLength: 8 } }}
          sx={{ mb: 2, mt: 2 }}
          disabled={countdown !== 0}
        />
        {countdown !== 0 && (
          <Typography
            sx={{ mb: 2, textAlign: 'center', color: COLORS.SAFEGUARD_BLUE }}
          >
            {t(StringKeys.SUCCESS_PAGE_WILL_REFRESH_IN_COUNTDOWN, {
              countdown,
            })}
          </Typography>
        )}
      </BaseModal>

      <EnterPinModal
        show={showPinModal && countdown === 0}
        onHide={() => setShowPinModal(false)}
        handleEnterPin={handleEnterPin}
      />
      <EnterGroupModal
        show={showGroupModal && countdown === 0}
        onHide={() => setShowGroupModal(false)}
        groups={groups}
        handleEnterGroup={handleEnterGroup}
      />
      <LoadingData show={loading} />
    </>
  );
}

export default UpdateOrganizationModal;
